import { Table, TableColumn, useViewportSize } from '@ole-ui/ole-ui-components';
import React, { useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import useUserTimezone from 'hooks/useUserTimezone';
import {
  EnhancedLiveClassOffering,
  LiveClassTableStatus,
} from 'types/liveClass';
import { VocabularyItem } from 'types';
import { Flex, Tooltip } from '@patternfly/react-core';
import {
  // OutlinedClockIcon as ScheduledClassIcon,
  ExclamationTriangleIcon,
  InfoCircleIcon,
  QuestionCircleIcon,
} from '@patternfly/react-icons';
import { Link } from 'react-mobx-router5';
import StartDateCell from './Cells/StartDateCell';
import EndDateCell from './Cells/EndDateCell';
import DayPatternCell from './Cells/DayPatternCell';
import LanguageCell from './Cells/LanguageCell';
import StatusCell from './Cells/StatusCell';
import SeatsAvailableCell from './Cells/SeatsAvailableCell';
import GuaranteedToRunCell from './Cells/GuaranteedToRunCell';
import DeliveryDaysExpand from './DeliveryDaysExpand';

type OverlapOffering = {
  routeName: string;
  routeParams: Record<string, string>;
  title?: string;
};

type Props = {
  data?: EnhancedLiveClassOffering[];
  languages?: VocabularyItem[];
  getOfferingStatus?: (
    offering: EnhancedLiveClassOffering,
  ) => LiveClassTableStatus | null;
  getGuaranteedToRunLabel?: (
    offering: EnhancedLiveClassOffering,
  ) => React.ReactNode;
  onRowSelected?: (offering: EnhancedLiveClassOffering) => void;
  isRowDisabled?: (row: EnhancedLiveClassOffering) => boolean;
  isNotSupportedBySubscription?: boolean;
  overlapEnrolledOfferings?: Record<string, OverlapOffering>;
  enrolledOfferingId?: string;
  isPartnerSchedule?: boolean;
  /** Tooltip message when row is disabled (e.g. full, quota). Null if another icon is shown. */
  getDisabledRowTooltip?: (
    offering: EnhancedLiveClassOffering,
  ) => string | null;
};

const paginationOptions = {
  widgetId: 'live-class-schedule-page-pagination-options-menu',
  perPageOptions: [
    { title: '5', value: 5 },
    { title: '10', value: 10 },
    { title: '25', value: 25 },
  ],
};

export default function ScheduleTable({
  data,
  languages,
  getOfferingStatus,
  getGuaranteedToRunLabel,
  onRowSelected,
  isRowDisabled,
  isNotSupportedBySubscription,
  overlapEnrolledOfferings = {},
  enrolledOfferingId,
  isPartnerSchedule = false,
  getDisabledRowTooltip,
}: Props) {
  const { t } = useTranslation();
  const { userTimezone } = useUserTimezone();
  const isMobile = useViewportSize().isLowerThanMd;

  const columnDefinitions = useMemo<TableColumn<EnhancedLiveClassOffering>[]>(
    () => [
      {
        id: 'access-warning',
        disableSortBy: true,
        patternflyThProps: {
          modifier: 'fitContent',
          style: {
            paddingRight: 0,
          },
        },
        Cell: ({ row }) => {
          const offeringId = row.original.offering_id;
          const overlapOffering = overlapEnrolledOfferings[offeringId];

          if (overlapOffering) {
            const classLink = (
              <Link
                href
                routeName={overlapOffering.routeName}
                routeParams={overlapOffering.routeParams}
                style={{
                  color: 'var(--pf-v5-global--palette--blue-200)',
                }}
              />
            );

            return (
              <Tooltip
                content={
                  <div>
                    <Trans
                      i18nKey="The dates and times of this class overlap with your existing enrollment in the class <classLink>{{className}}</classLink>. To schedule this class, please cancel a scheduled one or attend a scheduled enrollment first."
                      values={{ className: overlapOffering.title }}
                      components={{ classLink }}
                    />
                  </div>
                }
              >
                <span
                  style={{
                    display: 'flex',
                    color: 'var(--pf-v5-global--warning-color--100)',
                  }}
                >
                  <ExclamationTriangleIcon color="var(--pf-v5-global--warning-color--100)" />
                </span>
              </Tooltip>
            );
          }

          const disabledTooltip = getDisabledRowTooltip?.(row.original);
          if (disabledTooltip) {
            return (
              <Tooltip content={<div>{disabledTooltip}</div>}>
                <span
                  style={{
                    display: 'flex',
                    color: 'var(--pf-v5-global--warning-color--100)',
                  }}
                >
                  <ExclamationTriangleIcon color="var(--pf-v5-global--warning-color--100)" />
                </span>
              </Tooltip>
            );
          }

          if (!isNotSupportedBySubscription) {
            return null;
          }

          return (
            <Tooltip
              content={
                <div>
                  {isPartnerSchedule
                    ? t(
                        'The selected user does not have access to schedule this class.',
                      )
                    : t('You do not have access to schedule this class.')}
                </div>
              }
            >
              <span
                style={{
                  display: 'flex',
                  color: 'var(--pf-v5-global--danger-color--100)',
                }}
              >
                <ExclamationTriangleIcon color="var(--pf-v5-global--danger-color--100)" />
              </span>
            </Tooltip>
          );
        },
      },
      {
        Header: isMobile
          ? t('Start Time')
          : () => (
              <Flex
                alignItems={{ default: 'alignItemsCenter' }}
                gap={{ default: 'gapSm' }}
                flexWrap={{ default: 'nowrap' }}
              >
                {t('Start Time')}{' '}
                <Tooltip
                  content={t(
                    'The start time is based on your time zone: {{- timezone}}',
                    {
                      timezone: userTimezone || t('unknown'),
                    },
                  )}
                >
                  <QuestionCircleIcon
                    color="var(--pf-v5-global--Color--200)"
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Flex>
            ),
        accessor: 'start_time',
        Cell: (props) => <StartDateCell {...props} timezone={userTimezone} />,
        patternflyThProps: { modifier: 'nowrap' },
        sort: true,
      },
      {
        Header: t('End time'),
        accessor: 'end_time',
        Cell: (props) => <EndDateCell {...props} timezone={userTimezone} />,
        patternflyThProps: { modifier: 'nowrap' },
      },
      ...(isPartnerSchedule
        ? [
            {
              id: 'offering_id',
              Header: t('Offering ID'),
              accessor: 'offering_id',
              disableSortBy: true,
              patternflyThProps: { modifier: 'nowrap' },
            } as TableColumn<EnhancedLiveClassOffering>,
          ]
        : []),
      {
        Header: t('Day pattern'),
        accessor: 'class_type',
        disableSortBy: true,
        patternflyThProps: { modifier: 'nowrap' },
        Cell: (props) => <DayPatternCell {...props} />,
      },
      {
        Header: t('Language'),
        accessor: 'instruction_language',
        disableSortBy: true,
        Cell: (props) => (
          <LanguageCell {...props} languages={languages || []} />
        ),
      },
      {
        Header: t('Version'),
        accessor: 'offering_version',
      },
      {
        Header: t('Status'),
        accessor: 'lms_seat_status',
        disableSortBy: true,
        Cell: (props) => (
          <StatusCell {...props} getOfferingStatus={getOfferingStatus} />
        ),
      },
      ...(isPartnerSchedule
        ? [
            {
              id: 'lms_seats_available',
              Header: t('Seats available'),
              accessor: 'lms_seats_available',
              sort: true,
              patternflyThProps: { modifier: 'nowrap' },
              Cell: (props) => (
                <SeatsAvailableCell
                  {...props}
                  getOfferingStatus={getOfferingStatus}
                />
              ),
            } as TableColumn<EnhancedLiveClassOffering>,
          ]
        : []),
      {
        Header: isMobile
          ? t('Guaranteed to run')
          : () => (
              <Flex
                alignItems={{ default: 'alignItemsCenter' }}
                gap={{ default: 'gapSm' }}
                flexWrap={{ default: 'nowrap' }}
              >
                {t('Guaranteed to run')}
                <Tooltip
                  content={t(
                    'Classes marked as Guaranteed to run are not subject to cancellation.',
                  )}
                >
                  <InfoCircleIcon
                    color="var(--pf-v5-global--info-color--100)"
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Flex>
            ),
        accessor: 'status',
        disableSortBy: true,
        patternflyThProps: { modifier: 'nowrap' },
        Cell: (props) => (
          <GuaranteedToRunCell
            {...props}
            getGuaranteedToRunLabel={getGuaranteedToRunLabel}
          />
        ),
      },
    ],
    [
      t,
      userTimezone,
      overlapEnrolledOfferings,
      isNotSupportedBySubscription,
      enrolledOfferingId,
      getDisabledRowTooltip,
      isPartnerSchedule,
    ],
  );

  // Start time header has info tooltip "The start time is based on your time zone..."
  // TODO: Default sort start time ascending

  const handleSelectedRowsChange = (items: EnhancedLiveClassOffering[]) => {
    const selected = items?.[0] || null;
    if (selected) {
      onRowSelected(selected);
    }
  };

  const renderDeliveryDaysSubRow = (row: {
    original: EnhancedLiveClassOffering;
  }) => <DeliveryDaysExpand offering={row.original} timezone={userTimezone} />;

  return (
    <Table
      ariaLabel={t('Table containing ...')}
      className="course-progress__table live-class-schedule-table"
      data={data || []}
      columns={columnDefinitions}
      paginationOptions={paginationOptions}
      isStriped
      isExpandable
      isSelectable
      selectVariant="radio"
      onSelectedRowsChange={handleSelectedRowsChange}
      // renderEmptyState={renderEmptyState}
      variant="compact"
      isRowDisabled={(row) =>
        isRowDisabled ? isRowDisabled(row.original) : false
      }
      initialSortBy={[{ id: 'start_time', desc: false }]}
      selectionDataAnalyticsId="radio-btn-schedule-table-lp"
      subRowOptions={{
        render: renderDeliveryDaysSubRow,
        noPadding: true,
      }}
    />
  );
}
